import React, { useState } from 'react';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: 'automation',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setFormData({ name: '', email: '', company: '', service: 'automation', message: '' });
    setIsSubmitting(false);
    
    setTimeout(() => setSubmitted(false), 5000);
  };

  const services = [
    { value: 'automation', label: 'Workflow Automation' },
    { value: 'ai', label: 'AI Integration' },
    { value: 'data', label: 'Data Pipelines' },
    { value: 'custom', label: 'Custom Development' },
    { value: 'cloud', label: 'Cloud Infrastructure' },
    { value: 'consulting', label: 'Consulting & Strategy' },
    { value: 'other', label: 'Other' },
  ];

  return (
    <section id="contact" className="relative py-24 lg:py-32 px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-maize-500/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-4xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="reveal inline-block px-4 py-1.5 text-sm font-medium text-maize-500 bg-maize-500/10 rounded-full mb-4">
            Get in Touch
          </span>
          <h2 className="reveal text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            <span className="text-white">Let's </span>
            <span className="gradient-text">Collaborate</span>
          </h2>
          <p className="reveal text-gray-400 text-lg max-w-xl mx-auto">
            Ready to transform your business? We'd love to hear from you. 
            Send us a message and we'll get back to you shortly.
          </p>
        </div>

        {/* Contact Form */}
        <div className="reveal glass gradient-border rounded-2xl p-8 sm:p-12">
          {submitted && (
            <div className="mb-8 p-6 bg-gradient-to-r from-maize-500/20 to-green-500/20 border border-maize-500/30 rounded-xl flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-maize-500/20 rounded-full">
                <svg className="w-6 h-6 text-maize-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-1">Message Sent Successfully!</h4>
                <p className="text-gray-400 text-sm">Thank you for reaching out. We'll get back to you within 24 hours.</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name & Email Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-dark-900/50 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-gray-500 focus:outline-none focus:border-maize-500/50 focus:ring-2 focus:ring-maize-500/20 transition-all duration-300"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-dark-900/50 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-gray-500 focus:outline-none focus:border-maize-500/50 focus:ring-2 focus:ring-maize-500/20 transition-all duration-300"
                  placeholder="john@example.com"
                />
              </div>
            </div>

            {/* Company & Service Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                  Company
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full bg-dark-900/50 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-gray-500 focus:outline-none focus:border-maize-500/50 focus:ring-2 focus:ring-maize-500/20 transition-all duration-300"
                  placeholder="Your Company"
                />
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-medium text-gray-300 mb-2">
                  Service Interested In
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full bg-dark-900/50 border border-white/10 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:border-maize-500/50 focus:ring-2 focus:ring-maize-500/20 transition-all duration-300 appearance-none cursor-pointer"
                >
                  {services.map((service) => (
                    <option key={service.value} value={service.value} className="bg-dark-900">
                      {service.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Message Field */}
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                Message *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={5}
                className="w-full bg-dark-900/50 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-gray-500 focus:outline-none focus:border-maize-500/50 focus:ring-2 focus:ring-maize-500/20 transition-all duration-300 resize-none"
                placeholder="Tell us about your project and how we can help..."
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full group relative inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-maize-400 to-maize-500 text-dark-950 font-bold text-lg rounded-xl hover:shadow-xl hover:shadow-maize-400/25 transition-all duration-300 hover:-translate-y-1 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <span>Send Message</span>
                  <svg className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </>
              )}
            </button>
          </form>

          {/* Contact Info */}
          <div className="mt-10 pt-8 border-t border-white/10 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-maize-500/10 rounded-xl">
                <svg className="w-5 h-5 text-maize-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <p className="text-gray-500 text-sm">Email</p>
                <a href="mailto:contact@automaize.tech" className="text-white font-medium hover:text-maize-400 transition-colors">
                  info@automaize.tech
                </a>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {/* <div className="w-12 h-12 flex items-center justify-center bg-maize-500/10 rounded-xl">
                <svg className="w-5 h-5 text-maize-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div> */}
              {/* <div>
                <p className="text-gray-500 text-sm">Phone</p>
                <a href="tel:+1234567890" className="text-white font-medium hover:text-maize-400 transition-colors">
                  +1 (234) 567-890
                </a>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;